import { AppRouteRecordRaw } from '../types';
import { PARTNER_TYPES } from '@/constants/partner-types';
import {
  AI_OPERATIONS_ADMIN_ROLES,
  AI_OPERATIONS_VIEW_ROLES,
  LEGACY_REQUEST_INBOX_BASE_PATH,
  REQUEST_INBOX_BASE_PATH,
  REQUEST_INBOX_ROUTE_NAMES,
} from '@/constants/request-inbox';
import { Roles } from '@/constants/roles';
import { AI_TRAINING_BASE_PATH, AI_TRAINING_ROUTE_NAMES } from '@/constants/ai-training';
import {
  AI_ASSISTANTS_BASE_PATH,
  AI_ASSISTANTS_HOW_IT_WORKS_PATH,
  AI_ASSISTANTS_HOW_IT_WORKS_ROUTE_NAME,
  AI_ASSISTANTS_KNOWLEDGE_CENTER_PATH,
  AI_KNOWLEDGE_ROUTE_NAMES,
  AI_ASSISTANTS_OVERVIEW_PATH,
  AI_ASSISTANTS_OVERVIEW_ROUTE_NAME,
  LEGACY_AI_TRAINING_BASE_PATH,
  LEGACY_SUPPORT_CHAT_BASE_PATH,
  SUPPORT_CHAT_BASE_PATH,
  SUPPORT_CHAT_ROUTE_NAMES,
  movedPath,
} from '@/constants/ai-assistants';

const Body = () => import('@/components/body.vue');

const AiAssistantsOverview = () =>
  import('@/pages/backoffice/ai-assistants/overview/AiAssistantsOverview.vue');

const AiAssistantsHowItWorks = () =>
  import('@/pages/backoffice/ai-assistants/how-it-works/AiAssistantsHowItWorks.vue');

// Knowledge Center: read-only view of the AI knowledge collections and a retrieval preview.
const KnowledgeCollections = () =>
  import('@/pages/backoffice/ai-assistants/knowledge-center/collections/KnowledgeCollections.vue');
const KnowledgeArticles = () =>
  import('@/pages/backoffice/ai-assistants/knowledge-center/articles/KnowledgeArticles.vue');
const KnowledgeArticle = () =>
  import('@/pages/backoffice/ai-assistants/knowledge-center/article/KnowledgeArticle.vue');
const KnowledgeTestQuestion = () =>
  import('@/pages/backoffice/ai-assistants/knowledge-center/test/KnowledgeTestQuestion.vue');

// AI Training (spec v10 §15, D-61; v11 §16): training cases, rules and snapshots for Mia, the portal
// assistant and the partner assistants.
const TrainingCases = () =>
  import('@/pages/backoffice/ai-assistants/training/cases/TrainingCases.vue');
const TrainingCaseDetail = () =>
  import('@/pages/backoffice/ai-assistants/training/case-detail/TrainingCaseDetail.vue');
const TrainingAreaRules = () =>
  import('@/pages/backoffice/ai-assistants/training/area-rules/TrainingAreaRules.vue');
const TrainingActivation = () =>
  import('@/pages/backoffice/ai-assistants/training/activation/TrainingActivation.vue');
const TrainingRegressionRuns = () =>
  import('@/pages/backoffice/ai-assistants/training/regression-runs/TrainingRegressionRuns.vue');
const TrainingRegressionRunDetail = () =>
  import('@/pages/backoffice/ai-assistants/training/regression-runs/TrainingRegressionRunDetail.vue');

// Support chat (Mia, ryd-end-user-ai) administration.
const ChatAiStatus = () =>
  import('@/pages/backoffice/ai-assistants/support-chat/settings/ChatAiStatus.vue');
const OtpInterceptLookup = () =>
  import('@/pages/backoffice/ai-assistants/support-chat/verification-code/OtpInterceptLookup.vue');
const SupportChatAgentWhitelist = () =>
  import('@/pages/backoffice/ai-assistants/support-chat/agent-whitelist/SupportChatAgentWhitelist.vue');

// Request Inbox (moved from Customer Support): inbound requests per source, ticket drafts and routing.
const RequestInboxOverview = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/overview/RequestInboxOverview.vue');
const RequestInboxMessages = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/messages/RequestInboxMessages.vue');
const RequestInboxMessageDetail = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/message-detail/RequestInboxMessageDetail.vue');
const RequestInboxTaxonomy = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/taxonomy/RequestInboxTaxonomy.vue');
const RequestInboxRoutingRules = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/routing-rules/RequestInboxRoutingRules.vue');
const RequestInboxSources = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/sources/RequestInboxSources.vue');
const RequestInboxFeedback = () =>
  import('@/pages/backoffice/ai-assistants/request-inbox/feedback/RequestInboxFeedback.vue');

const internalOnly = {
  requiresAuth: true,
  requiredUserGroup: ['ryd'],
  requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
};

export const aiAssistantsRoutes: AppRouteRecordRaw[] = [
  {
    // The section root has no page of its own; it opens the overview. Keep query and hash.
    path: AI_ASSISTANTS_BASE_PATH,
    redirect: (to) => ({ name: AI_ASSISTANTS_OVERVIEW_ROUTE_NAME, query: to.query, hash: to.hash }),
  },
  {
    // Same audience as the folder in the menu: the AI operations roles plus customer support and
    // operations, because the overview links to both the training and the support chat tools.
    path: AI_ASSISTANTS_OVERVIEW_PATH,
    component: Body,
    meta: {
      ...internalOnly,
      requiredAnyRole: [
        ...AI_OPERATIONS_VIEW_ROLES,
        Roles.customer_support_admin,
        Roles.customer_support_agent,
        Roles.operations_admin,
      ],
      breadcrumb: 'AI Assistants',
    },
    children: [
      {
        path: '',
        name: AI_ASSISTANTS_OVERVIEW_ROUTE_NAME,
        component: AiAssistantsOverview,
        meta: { title: 'AI Assistants Overview | ryd Portal', breadcrumb: 'Overview' },
      },
    ],
  },
  {
    // Same guard as the overview: staff read how the Request Inbox, the knowledge center and AI training fit together.
    path: AI_ASSISTANTS_HOW_IT_WORKS_PATH,
    component: Body,
    meta: {
      ...internalOnly,
      requiredAnyRole: [
        ...AI_OPERATIONS_VIEW_ROLES,
        Roles.customer_support_admin,
        Roles.customer_support_agent,
        Roles.operations_admin,
      ],
      breadcrumb: 'AI Assistants',
    },
    children: [
      {
        path: '',
        name: AI_ASSISTANTS_HOW_IT_WORKS_ROUTE_NAME,
        component: AiAssistantsHowItWorks,
        meta: { title: 'AI Assistants How It Works | ryd Portal', breadcrumb: 'How It Works' },
      },
    ],
  },
  {
    // Same guard as the Request Inbox pages: ryd, RYD_INTERNAL and any ai_operations_* role. The API
    // (rp-integrations /ai-knowledge/*) checks ryd + ai_operations_* again.
    path: AI_ASSISTANTS_KNOWLEDGE_CENTER_PATH,
    component: Body,
    meta: {
      ...internalOnly,
      requiredAnyRole: AI_OPERATIONS_VIEW_ROLES,
      breadcrumb: 'AI Assistants',
    },
    children: [
      {
        path: '',
        name: AI_KNOWLEDGE_ROUTE_NAMES.collections,
        component: KnowledgeCollections,
        meta: { title: 'AI Knowledge Collections | ryd Portal', breadcrumb: 'Knowledge Center' },
      },
      {
        path: 'collections/:corpus',
        name: AI_KNOWLEDGE_ROUTE_NAMES.articles,
        component: KnowledgeArticles,
        meta: { title: 'AI Knowledge Articles | ryd Portal', breadcrumb: 'Articles' },
      },
      {
        // The article key goes in the query: keys are paths and contain slashes.
        path: 'collections/:corpus/article',
        name: AI_KNOWLEDGE_ROUTE_NAMES.article,
        component: KnowledgeArticle,
        meta: { title: 'AI Knowledge Article | ryd Portal', breadcrumb: 'Article' },
      },
      {
        path: 'test',
        name: AI_KNOWLEDGE_ROUTE_NAMES.test,
        component: KnowledgeTestQuestion,
        meta: { title: 'AI Knowledge Test A Question | ryd Portal', breadcrumb: 'Test A Question' },
      },
    ],
  },
  {
    // Own record so the guard enforces ryd, RYD_INTERNAL and the ai_operations_* roles; agent and admin
    // actions are gated inside the pages and by the API.
    path: AI_TRAINING_BASE_PATH,
    component: Body,
    meta: { ...internalOnly, requiredAnyRole: AI_OPERATIONS_VIEW_ROLES, breadcrumb: 'AI Training' },
    children: [
      {
        path: '',
        redirect: (to) => ({ name: AI_TRAINING_ROUTE_NAMES.cases, query: to.query, hash: to.hash }),
      },
      {
        path: 'cases',
        name: AI_TRAINING_ROUTE_NAMES.cases,
        component: TrainingCases,
        meta: { title: 'Training Cases | ryd Portal', breadcrumb: 'Training Cases' },
      },
      {
        path: 'cases/:caseId',
        name: AI_TRAINING_ROUTE_NAMES.caseDetail,
        component: TrainingCaseDetail,
        props: true,
        meta: { title: 'Training Case | ryd Portal', breadcrumb: 'Training Case' },
      },
      {
        path: 'area-rules',
        name: AI_TRAINING_ROUTE_NAMES.areaRules,
        component: TrainingAreaRules,
        meta: { title: 'Training Area Rules | ryd Portal', breadcrumb: 'Area Rules' },
      },
      {
        path: 'activation',
        name: AI_TRAINING_ROUTE_NAMES.activation,
        component: TrainingActivation,
        meta: {
          title: 'Training Activation And Snapshots | ryd Portal',
          breadcrumb: 'Activation & Snapshots',
        },
      },
      {
        path: 'regression-runs',
        name: AI_TRAINING_ROUTE_NAMES.runs,
        component: TrainingRegressionRuns,
        meta: { title: 'Training Regression Runs | ryd Portal', breadcrumb: 'Regression Runs' },
      },
      {
        path: 'regression-runs/:runId',
        name: AI_TRAINING_ROUTE_NAMES.runDetail,
        component: TrainingRegressionRunDetail,
        props: true,
        meta: { title: 'Training Regression Run | ryd Portal', breadcrumb: 'Regression Run' },
      },
    ],
  },
  {
    // Settings and verification code are for customer support (gated in the menu, as before the move);
    // the agent whitelist keeps its own operations_admin requirement: it decides who may read customer
    // data in agent mode.
    path: SUPPORT_CHAT_BASE_PATH,
    component: Body,
    meta: { ...internalOnly, breadcrumb: 'Support Chat' },
    children: [
      {
        path: 'settings',
        name: SUPPORT_CHAT_ROUTE_NAMES.settings,
        component: ChatAiStatus,
        meta: { title: 'Support Chat Settings | ryd Portal', breadcrumb: 'Support Chat Settings' },
      },
      {
        path: 'verification-code',
        name: SUPPORT_CHAT_ROUTE_NAMES.verificationCode,
        component: OtpInterceptLookup,
        meta: {
          title: 'Chat Verification Code | ryd Portal',
          breadcrumb: 'Chat Verification Code',
        },
      },
      {
        path: 'agent-whitelist',
        name: SUPPORT_CHAT_ROUTE_NAMES.agentWhitelist,
        component: SupportChatAgentWhitelist,
        meta: {
          title: 'Support Chat Agent Whitelist | ryd Portal',
          breadcrumb: 'Support Chat Agent Whitelist',
          requiredRoles: ['operations_admin'],
        },
      },
    ],
  },
  {
    // Moved from Customer Support (2026-10-25). The round-3 `mia-cases` URLs become `cases`.
    path: `${LEGACY_AI_TRAINING_BASE_PATH}/:rest(.*)*`,
    redirect: (to) => ({
      path: movedPath(AI_TRAINING_BASE_PATH, to.params.rest, { 'mia-cases': 'cases' }),
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: `${LEGACY_SUPPORT_CHAT_BASE_PATH}/:rest(.*)*`,
    redirect: (to) => ({
      path: movedPath(SUPPORT_CHAT_BASE_PATH, to.params.rest),
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    // Request Inbox: its own record so the router guard (which checks the first matched record with
    // a requirement) enforces the ryd group, RYD_INTERNAL and the ai_operations_* roles (D-44).
    path: REQUEST_INBOX_BASE_PATH,
    component: Body,
    meta: {
      ...internalOnly,
      requiredAnyRole: AI_OPERATIONS_VIEW_ROLES,
      breadcrumb: 'Request Inbox',
    },
    children: [
      {
        path: '',
        name: REQUEST_INBOX_ROUTE_NAMES.overview,
        component: RequestInboxOverview,
        meta: {
          title: 'Request Inbox Overview | ryd Portal',
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'messages',
        name: REQUEST_INBOX_ROUTE_NAMES.messages,
        component: RequestInboxMessages,
        meta: {
          title: 'Request Inbox Messages | ryd Portal',
          breadcrumb: 'Messages',
        },
      },
      {
        path: 'messages/:messageId',
        name: REQUEST_INBOX_ROUTE_NAMES.messageDetail,
        component: RequestInboxMessageDetail,
        props: true,
        meta: {
          title: 'Request Inbox Message | ryd Portal',
          breadcrumb: 'Message',
        },
      },
      {
        path: 'taxonomy',
        name: REQUEST_INBOX_ROUTE_NAMES.taxonomy,
        component: RequestInboxTaxonomy,
        meta: {
          title: 'Request Inbox Taxonomy | ryd Portal',
          breadcrumb: 'Taxonomy',
        },
      },
      {
        path: 'routing-rules',
        name: REQUEST_INBOX_ROUTE_NAMES.routingRules,
        component: RequestInboxRoutingRules,
        meta: {
          title: 'Request Inbox Routing Rules | ryd Portal',
          breadcrumb: 'Routing Rules',
        },
      },
      {
        // Source registry admin tab (§13.6, D-51/D-52).
        path: 'sources',
        name: REQUEST_INBOX_ROUTE_NAMES.sources,
        component: RequestInboxSources,
        meta: {
          title: 'Request Inbox Sources | ryd Portal',
          breadcrumb: 'Sources',
          requiredAnyRole: AI_OPERATIONS_ADMIN_ROLES,
          strictRoles: true,
        },
      },
      {
        // The feedback queue API is admin-only (§12.2), so the page is too.
        path: 'feedback',
        name: REQUEST_INBOX_ROUTE_NAMES.feedback,
        component: RequestInboxFeedback,
        meta: {
          title: 'Request Inbox Feedback Queue | ryd Portal',
          breadcrumb: 'Feedback Queue',
          requiredAnyRole: AI_OPERATIONS_ADMIN_ROLES,
          strictRoles: true,
        },
      },
    ],
  },
  {
    // Moved from Customer Support. Every sub-path (messages/:messageId, taxonomy, ...) follows.
    path: `${LEGACY_REQUEST_INBOX_BASE_PATH}/:rest(.*)*`,
    redirect: (to) => ({
      path: movedPath(REQUEST_INBOX_BASE_PATH, to.params.rest),
      query: to.query,
      hash: to.hash,
    }),
  },
];
